import React, { useState } from 'react'
import { useMutation } from '@apollo/client'
import { Button as ButtonComponent } from '@/components/ui/button'
import { DISMISS_WARMUP_NOTICE } from '@/graphql/warmup-queries'
import { showNotification } from '@/utils/notifications'
import { warmupNotUsedText } from '@/utils/pixel-warmup-panel'

const Button = ButtonComponent as React.ComponentType<any>

export interface NotUsedNotice {
    visible: boolean
    dismissible: boolean
    dismissed: boolean
    reason: string | null
    since: string | null
    ends_at: string | null
}

interface WarmupNoticeProps {
    adCampaignId: string
    notice?: NotUsedNotice | null
    /** Called after the notice was closed, so the warm-ups are read again without it. */
    onDismissed: () => void
}

/**
 * The warm-up the owner chose could not be used and the campaign was launched without it. The server says when it is
 * shown (a few days from the launch, until the owner closes it); the owner can close it sooner.
 */
const WarmupNotice: React.FC<WarmupNoticeProps> = ({ adCampaignId, notice, onDismissed }) => {
    const [dismiss, { loading }] = useMutation(DISMISS_WARMUP_NOTICE)
    const [hidden, setHidden] = useState(false)
    if (!notice?.visible || hidden) return null

    return (
        <div
            role="status"
            data-testid="warmup-not-used-notice"
            className="tw:sticky tw:left-0 tw:flex tw:w-[1000px] tw:items-center tw:gap-3 tw:border-b tw:border-[#2B3B4C] tw:bg-[#0B1E33] tw:px-6 tw:py-2 tw:text-sm tw:text-yellow-300"
        >
            <span>{warmupNotUsedText(notice.reason)}</span>
            {notice.dismissible && (
                <Button
                    size="sm"
                    variant="secondary"
                    disabled={loading}
                    onClick={async () => {
                        try {
                            await dismiss({ variables: { adCampaignId } })
                            setHidden(true)
                            onDismissed()
                        } catch (error) {
                            showNotification(
                                'The notice could not be closed. Try again.',
                                'error',
                                5000,
                                null,
                                null,
                                true,
                                true,
                            )
                        }
                    }}
                >
                    Dismiss
                </Button>
            )}
        </div>
    )
}

export default WarmupNotice
